import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import api from "../lib/api";
import { zar } from "../lib/format";
import Revenue from "./Revenue";
import Invoices from "./Invoices";
import FixedExpenses from "./FixedExpenses";
import SalesCount from "./SalesCount";
import { InvoiceReviewPanel } from "./Stock";

function DailyBySupplier() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [rows, setRows] = useState([]);
  const load = () => api.get("/invoices/daily-by-supplier", { params: { start, end } })
    .then((r) => setRows(r.data)).catch(() => toast.error("Failed to load invoice breakdown"));
  useEffect(() => { load(); }, [start, end]);

  return (
    <div className="space-y-4" data-testid="daily-supplier-view">
      <div className="flex items-center gap-3">
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="daily-supplier-start" />
        <span className="text-muted-foreground text-sm">to</span>
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="daily-supplier-end" />
      </div>
      {rows.length === 0 && <p className="text-sm text-muted-foreground py-6">No invoices captured in this period.</p>}
      {rows.map((d) => (
        <div key={d.date} className="glass border border-border rounded-lg overflow-hidden" data-testid={`daily-supplier-${d.date}`}>
          <div className="px-4 py-2.5 border-b bg-accent/40 flex justify-between items-center">
            <span className="font-heading font-semibold text-sm">{d.date}</span>
            <span className="num text-sm font-semibold">{zar(d.day_total)}</span>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {d.suppliers.map((s) => (
                <tr key={s.supplier} className="border-b last:border-0">
                  <td className="px-4 py-2">{s.supplier}</td>
                  <td className="px-4 py-2 text-muted-foreground text-xs">
                    {s.invoices.map((i) => `#${i.invoice_number || "—"}${i.type === "credit_note" ? " (CN)" : ""}`).join(", ")}
                  </td>
                  <td className="px-4 py-2 num text-right">{zar(s.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export default function Finance() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "revenue";
  const esub = params.get("esub") || "invoices";
  const setTab = (v) => setParams(v === "revenue" ? { tab: v } : v === "sales" ? { tab: v } : { tab: v, esub });

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Finance</h1>
        <p className="text-muted-foreground mt-1">Revenue, tips, sales counts, invoices and fixed expenses in one place</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="revenue" data-testid="finance-tab-revenue">Revenue</TabsTrigger>
          <TabsTrigger value="sales" data-testid="finance-tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="finance-tab-expenses">Expenses</TabsTrigger>
        </TabsList>
        <TabsContent value="revenue" className="mt-4">
          <Revenue embedded />
        </TabsContent>
        <TabsContent value="sales" className="mt-4">
          <section>
            <h2 className="font-heading text-lg font-semibold mb-1">Daily sales count — stock depletion</h2>
            <p className="text-xs text-muted-foreground mb-3">
              How many of each dish sold that day — depletes ingredients through the recipe tree. Revenue &amp; tips are still captured under Revenue → Input.
            </p>
            <SalesCount embedded />
          </section>
        </TabsContent>
        <TabsContent value="expenses" className="mt-4">
          <Tabs value={esub} onValueChange={(v) => setParams({ tab: "expenses", esub: v })}>
            <TabsList data-testid="expenses-tabs">
              <TabsTrigger value="invoices" data-testid="expenses-tab-invoices">Invoices</TabsTrigger>
              <TabsTrigger value="review" data-testid="expenses-tab-review">Invoice Review</TabsTrigger>
              <TabsTrigger value="daily" data-testid="expenses-tab-daily">By day &amp; supplier</TabsTrigger>
              <TabsTrigger value="fixed" data-testid="expenses-tab-fixed">Fixed Expenses</TabsTrigger>
            </TabsList>
            <TabsContent value="invoices" className="mt-4"><Invoices embedded /></TabsContent>
            <TabsContent value="review" className="mt-4">
              <p className="text-xs text-muted-foreground mb-3">Approve captured invoices — check the photo, match line items to stock, categorize the spend, then confirm to post to inventory.</p>
              <InvoiceReviewPanel />
            </TabsContent>
            <TabsContent value="daily" className="mt-4"><DailyBySupplier /></TabsContent>
            <TabsContent value="fixed" className="mt-4"><FixedExpenses embedded /></TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
}
